import {
	Attachment,
	AttachmentStatus,
	AttachmentType,
	type FileAttachment,
} from '@/states/attachment'
import { getImageDimensions, getVideoMetadata } from '@/utils/media'
import uploadFile, { CDNFileType } from '@/utils/upload'
import { cloneDeep } from 'lodash-es'
import toast from 'react-hot-toast'
import { useDebounce, useUnmount } from 'react-use'

interface AttachmentsUploadServiceProps {
	data: Attachment[]
	onUpdate?: (index: number, attachment: Attachment) => void
}

export default function AttachmentsUploadService({
	data,
	onUpdate,
}: AttachmentsUploadServiceProps) {
	const [isReady, cancel] = useDebounce(
		() => {
			const uploadAttachment = async (attachment: FileAttachment, index: number) => {
				const clonedAttachment = cloneDeep(attachment)

				await toast.promise(
					(async () => {
						// set metadata
						if (attachment.type === AttachmentType.PICTURE) {
							const { width, height } = await getImageDimensions(attachment.file)

							clonedAttachment.metadata = { width, height }
							clonedAttachment.status = AttachmentStatus.UPLOADING
							onUpdate?.(index, clonedAttachment)
							const cdnFile = await uploadFile(attachment.file, {
								provisionConfig: {
									uploadType: CDNFileType.PIC,
									bucket: 'jike',
								},
							})

							return cdnFile
						} else if (attachment.type === AttachmentType.VIDEO) {
							const { width, height, url } = await getVideoMetadata(attachment.file)

							clonedAttachment.metadata = { width, height }
							clonedAttachment.status = AttachmentStatus.UPLOADING
							onUpdate?.(index, clonedAttachment)
							const cdnFile = await uploadFile(attachment.file, {
								provisionConfig: {
									uploadType: CDNFileType.VIDEO,
									bucket: 'videos',
								},
							})

							return { ...cdnFile, fileUrl: url }
						}

						throw new Error('Unknown attachment type')
					})(),
					{
						loading: '正在上传',
						success: (cdnFile) => {
							clonedAttachment.status = AttachmentStatus.UPLOADED
							clonedAttachment.cdn = {
								file: cdnFile,
							}
							onUpdate?.(index, clonedAttachment)

							return '上传成功'
						},
						error: () => {
							clonedAttachment.status = AttachmentStatus.UPLOAD_FAILED
							onUpdate?.(index, clonedAttachment)

							return '上传失败'
						},
					}
				)
			}

			data.forEach((attachment, index) => {
				if (
					attachment.type !== AttachmentType.PICTURE &&
					attachment.type !== AttachmentType.VIDEO
				) {
					return
				}
				if (attachment.status !== AttachmentStatus.WAIT_TO_UPLOAD) {
					return
				}
				uploadAttachment(attachment, index)
			})
		},
		300,
		[data.length]
	)

	useUnmount(cancel)

	return null
}
